/**
 * Pagina de detalhe/edicao de usuario. Rota /dashboard/usuarios/:id
 * (id = "novo" pra criacao). Substitui o Drawer anterior — mesmo
 * padrao aplicado em ClientDetailPage.
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, useToast } from "@payflow/ui";
import { UserForm } from "./UsersPage";
import {
  createUser,
  updateUser,
  getUser,
  type UserResponse,
  type CreateUserData,
  type UpdateUserData,
} from "../../services/users.service";
import { listRoles, type RoleResponse } from "../../services/roles.service";
import { createInvite, type Invite } from "../../services/webauthn.service";
import { InviteLinkDialog } from "../../components/InviteLinkDialog";

export function UserDetailPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === "novo";

  const [user, setUser] = useState<UserResponse | null>(null);
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Convite gerado apos criar um novo usuario — dispara modal com link/mailto.
  const [invite, setInvite] = useState<Invite | null>(null);
  const [invitedUser, setInvitedUser] = useState<UserResponse | null>(null);

  useEffect(() => {
    listRoles({ limit: 100 })
      .then((r) => setRoles(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setLoading(true);
    getUser(id!)
      .then((u) => {
        if (!cancelled) setUser(u);
      })
      .catch(() => {
        if (!cancelled) {
          toast({ variant: "error", title: t("users.load_error") });
          navigate("/dashboard/usuarios", { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate, t, toast]);

  const handleSave = async (data: CreateUserData | UpdateUserData) => {
    setSaving(true);
    try {
      if (user) {
        await updateUser(user.id, data as UpdateUserData);
        toast({ variant: "success", title: t("users.update_success") });
        navigate("/dashboard/usuarios");
      } else {
        const created = await createUser(data as CreateUserData);
        toast({ variant: "success", title: t("users.create_success") });

        // Gera convite pra novo usuario cadastrar sua primeira passkey.
        // Falha aqui nao inviabiliza a criacao — mostra warning e segue.
        try {
          const inv = await createInvite(created.id);
          setInvite(inv);
          setInvitedUser(created);
        } catch (err) {
          toast({
            variant: "error",
            title: err instanceof Error ? err.message : t("invite.create_error"),
          });
          navigate("/dashboard/usuarios");
        }
      }
    } catch (err) {
      toast({
        variant: "error",
        title: err instanceof Error ? err.message : t("users.save_error"),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInviteClose = () => {
    setInvite(null);
    setInvitedUser(null);
    navigate("/dashboard/usuarios");
  };

  return (
    <div className="flex min-h-[calc(100vh-8rem)] flex-col gap-4">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/dashboard/usuarios")}
          className="h-9 w-9 p-0"
          aria-label={t("common.back")}
        >
          <BackIcon />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {user ? t("users.edit_title") : t("users.new_title")}
          </h1>
          {user && <p className="text-sm text-muted-foreground">{user.nome}</p>}
        </div>
      </div>

      <div className="flex flex-1 flex-col rounded-lg border border-border bg-card px-6 py-4">
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
          </div>
        ) : (
          <UserForm
            variant="page"
            user={user}
            roles={roles}
            saving={saving}
            onSave={handleSave}
          />
        )}
      </div>

      {invite && invitedUser && (
        <InviteLinkDialog
          open={!!invite}
          onOpenChange={(open) => {
            if (!open) handleInviteClose();
          }}
          url={invite.url}
          userName={invitedUser.nome}
          userEmail={invitedUser.email}
          expiresAt={invite.expiresAt}
        />
      )}
    </div>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
